<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import GroupedRow from "@component/GroupedRow.vue";
import RowGroup from "@component/RowGroup.vue";
import SingleRow from "@component/SingleRow.vue";
import type { ReportListData, ServiceReportItem } from "@entity/ServiceRow";

defineOptions({
  inheritAttrs: false,
});

interface Props {
  report: ServiceReportItem[];
  reportData: ReportListData;
  service: string;
  indexReport: string;
  mobileMultiple?: boolean;
  region?: number;
  regionNames: string[];
}

interface RowSelection {
  ids: Array<number | undefined>;
  regionNames: string[];
}

interface GroupSelection {
  ids: number[][];
  regionNames: string[];
}

const props = withDefaults(defineProps<Props>(), {
  mobileMultiple: false,
  region: 0,
});

const groupedServiceNames = new Set([
  "Calling",
  "Phones",
  "Meetings",
  "SMS",
  "Fax",
  "Engage",
  "Analytics",
]);

const { t } = useI18n();

const subServices = computed(() => Object.keys(props.reportData));
const isGroupedReport = computed(() => subServices.value.length > 1);
const isSingleGroupedService = computed(() =>
  subServices.value.length === 1 && groupedServiceNames.has(props.service),
);
const componentId = computed(() =>
  props.mobileMultiple ? getId(`${props.region}-${props.indexReport}`) : getId(props.indexReport),
);
const rowGroupSelection = computed(() => getGroupSelection(props.reportData));
const rowSelections = computed<Record<string, RowSelection>>(() =>
  subServices.value.reduce<Record<string, RowSelection>>((selectionMap, subService) => {
    selectionMap[subService] = getRowSelection(subService);
    return selectionMap;
  }, {}),
);
const visibleSubServices = computed(() => subServices.value.filter((service) =>
  !props.mobileMultiple || rowSelections.value[service].ids.length > 0,
));

// Selection helpers prepare aligned ids and region names for row components.
function getGroupSelection(data: ReportListData): GroupSelection {
  const groupedIds: number[][] = [];

  Object.keys(data).forEach((service, key) => {
    data[service].ids.forEach((id, idKey) => {
      if (id === undefined) {
        return;
      }

      if (!groupedIds[idKey]) {
        groupedIds[idKey] = [];
      }

      groupedIds[idKey][key] = id;
    });
  });

  if (props.mobileMultiple) {
    const regionName = props.regionNames[props.region];
    const ids = groupedIds[props.region]?.filter((id) => id !== undefined) ?? [];

    return regionName && ids.length > 0
      ? { ids: [ids], regionNames: [regionName] }
      : { ids: [], regionNames: [] };
  }

  // Keep an empty slot for unavailable regions so later columns cannot shift.
  return {
    ids: props.regionNames.map((_, index) => groupedIds[index]?.filter((id) => id !== undefined) ?? []),
    regionNames: props.regionNames,
  };
}

function getRowSelection(subService: string): RowSelection {
  const ids = props.reportData[subService]?.ids ?? [];

  if (props.mobileMultiple) {
    const id = ids[props.region];
    const regionName = props.regionNames[props.region];

    return id !== undefined && regionName
      ? { ids: [id], regionNames: [regionName] }
      : { ids: [], regionNames: [] };
  }

  return {
    ids: props.regionNames.map((_, index) => ids[index]),
    regionNames: props.regionNames,
  };
}

// Render value helpers mirror the legacy template formatting.
function getId(index: string): string {
  return `chk${index}`;
}

function getServiceName(service: string, subService = ""): string {
  return subService ? `${service} - ${subService}` : service;
}

// Translation helpers keep the legacy i18n fallback behavior.
function serviceNameConstructor(serviceName: string): string {
  const rawName = serviceName.toLowerCase().split(" ");
  for (let i = 1; i < rawName.length; i += 1) {
    const word = rawName[i];
    rawName[i] = word.charAt(0).toUpperCase() + word.slice(1);
  }

  return rawName.join("");
}

function translateIfExists(key: string, fallback: string): string {
  const translated = t(key);
  return translated === key ? fallback : translated;
}

function getTranslatedServiceName(serviceName: string, isSingleRow = false): string {
  if (isSingleRow) {
    return translateIfExists(serviceName, serviceName);
  }

  const serviceKey = `service.${serviceNameConstructor(serviceName)}`;
  return translateIfExists(serviceKey, serviceName);
}
</script>

<template>
  <div v-if="visibleSubServices.length">
    <RowGroup
      v-if="isGroupedReport"
      :id="componentId"
      :text="getTranslatedServiceName(props.service)"
      :service="getServiceName(props.service)"
      :report="props.report"
      :ids="rowGroupSelection.ids"
      :region-names="rowGroupSelection.regionNames"
    >
      <GroupedRow
        v-for="subService in visibleSubServices"
        :key="`${componentId}-${subService}`"
        :text="getTranslatedServiceName(subService)"
        :service="getServiceName(props.service, subService)"
        :report="props.report"
        :ids="rowSelections[subService]?.ids ?? []"
        :region-names="rowSelections[subService]?.regionNames ?? []"
      />
    </RowGroup>

    <template v-else-if="isSingleGroupedService">
      <SingleRow
        v-for="subService in visibleSubServices"
        :id="componentId"
        :key="`${componentId}-${subService}`"
        :text="getTranslatedServiceName(subService, true)"
        :service="getServiceName(props.service, subService)"
        :report="props.report"
        :ids="rowSelections[subService]?.ids ?? []"
        :region-names="rowSelections[subService]?.regionNames ?? []"
      />
    </template>

    <SingleRow
      v-else
      :id="componentId"
      :text="getTranslatedServiceName(props.service, true)"
      :service="getServiceName(props.service)"
      :report="props.report"
      :ids="rowSelections[props.service]?.ids ?? []"
      :region-names="rowSelections[props.service]?.regionNames ?? []"
    />
  </div>
</template>
